import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User } from 'lucide-react';
import api from '../../services/api';

/**
 * MentionAutocomplete - Dropdown for @mention user autocomplete
 *
 * Props:
 * - isOpen: boolean - whether the dropdown is visible
 * - searchQuery: string - the text after @ to search for
 * - onSelect: (user) => void - callback when a user is selected
 * - onClose: () => void - callback to close the dropdown
 * - position: { top, left } - position for the dropdown
 */
const MentionAutocomplete = ({ isOpen, searchQuery, onSelect, onClose, position }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dropdownRef = useRef(null);

  // Fetch users when search query changes
  useEffect(() => {
    if (!isOpen) return;

    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await api.get('/users/firm-users/autocomplete', {
          params: { query: searchQuery, limit: 10 }
        });
        setUsers(response.data || []);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Error fetching users for mention:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchUsers, 150);
    return () => clearTimeout(debounceTimer);
  }, [isOpen, searchQuery]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen || users.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % users.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + users.length) % users.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (users[selectedIndex]) {
          onSelect(users[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
      case 'Tab':
        e.preventDefault();
        if (users[selectedIndex]) {
          onSelect(users[selectedIndex]);
        }
        break;
      default:
        break;
    }
  }, [isOpen, users, selectedIndex, onSelect, onClose]);

  // Attach keyboard listener to window
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[200px] max-w-[300px] max-h-[200px] overflow-y-auto"
      style={{
        top: position?.top || 0,
        left: position?.left || 0
      }}
    >
      {loading ? (
        <div className="px-3 py-2 text-sm text-gray-500">
          Searching...
        </div>
      ) : users.length === 0 ? (
        <div className="px-3 py-2 text-sm text-gray-500">
          {searchQuery ? 'No users found' : 'Type to search users'}
        </div>
      ) : (
        users.map((user, index) => (
          <button
            key={user.id}
            type="button"
            onClick={() => onSelect(user)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-100 ${
              index === selectedIndex ? 'bg-gray-50' : ''
            }`}
          >
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.display_name}
                className="w-6 h-6 rounded-full"
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="w-4 h-4 text-gray-500" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">
                {user.display_name}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {user.email}
              </div>
            </div>
          </button>
        ))
      )}
    </div>
  );
};

/**
 * useMention - Custom hook for handling @mention logic in a textarea
 *
 * Returns:
 * - mentionState: { isOpen, searchQuery, position, cursorPosition }
 * - handleInputChange: (e) => void - call this on textarea onChange
 * - handleMentionSelect: (user) => void - call when user selects from dropdown
 * - closeMention: () => void - close the dropdown
 * - insertMention: (text, cursorPos, user) => { newText, newCursorPos }
 */
export const useMention = (textareaRef) => {
  const [mentionState, setMentionState] = useState({
    isOpen: false,
    searchQuery: '',
    position: { top: 0, left: 0 },
    cursorPosition: 0,
    mentionStart: -1
  });

  const handleInputChange = useCallback((e, currentValue) => {
    const textarea = e.target;
    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = currentValue.slice(0, cursorPos);

    // Find the last @ before cursor that's not inside a completed mention
    const atIndex = textBeforeCursor.lastIndexOf('@');

    if (atIndex === -1) {
      // No @ found, close dropdown
      setMentionState(prev => ({ ...prev, isOpen: false }));
      return;
    }

    // Check if @ is at start or preceded by whitespace
    const charBeforeAt = atIndex > 0 ? textBeforeCursor[atIndex - 1] : ' ';
    const isValidMentionStart = charBeforeAt === ' ' || charBeforeAt === '\n' || atIndex === 0;

    if (!isValidMentionStart) {
      setMentionState(prev => ({ ...prev, isOpen: false }));
      return;
    }

    // Get the text after @ (the search query)
    const textAfterAt = textBeforeCursor.slice(atIndex + 1);

    // If there's a space after the name, the mention is complete
    // Allow multi-word names by not closing on space
    // Close only if we encounter a non-alphanumeric, non-space character
    const invalidChars = /[^a-zA-Z\s]/;
    if (invalidChars.test(textAfterAt) && textAfterAt.length > 0) {
      setMentionState(prev => ({ ...prev, isOpen: false }));
      return;
    }

    // Calculate position for dropdown (below cursor)
    const rect = textarea.getBoundingClientRect();
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 20;

    // Simple position calculation - in practice you'd want more sophisticated logic
    const lines = textBeforeCursor.split('\n');
    const currentLineIndex = lines.length - 1;

    setMentionState({
      isOpen: true,
      searchQuery: textAfterAt.trim(),
      position: {
        top: rect.top + ((currentLineIndex + 1) * lineHeight) + 5,
        left: rect.left + 10
      },
      cursorPosition: cursorPos,
      mentionStart: atIndex
    });
  }, []);

  const insertMention = useCallback((currentText, user) => {
    const { mentionStart, cursorPosition } = mentionState;

    if (mentionStart === -1) return { newText: currentText, newCursorPos: cursorPosition };

    const mentionText = `@${user.first_name} ${user.last_name}`;
    const textBefore = currentText.slice(0, mentionStart);
    const textAfter = currentText.slice(cursorPosition);

    const newText = textBefore + mentionText + ' ' + textAfter;
    const newCursorPos = mentionStart + mentionText.length + 1;

    return { newText, newCursorPos };
  }, [mentionState]);

  const closeMention = useCallback(() => {
    setMentionState(prev => ({ ...prev, isOpen: false, mentionStart: -1 }));
  }, []);

  return {
    mentionState,
    handleInputChange,
    insertMention,
    closeMention
  };
};

export default MentionAutocomplete;
